import { Link, useNavigate } from 'react-router';
import {
  Badge,
  Button,
  DatatableCell,
  DatatableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { MoreVerticalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PreResolvedAvatarCell } from '@/shared/ui/data-table';
import { DisabledReasonTooltip } from '@/shared/ui/DisabledReasonTooltip';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { ROUTES } from '@/shared/config/routes';
import { selectDistinctiveTraits } from '../../lib';
import { AudienceStatusChip } from '../AudienceStatusChip';
import type { AudiencesTableV2RowProps } from './AudiencesTableV2.types';

const EM_DASH = '—';

export function AudiencesTableV2Row({
  persona,
  gwiEnabled,
  showActions,
  onEdit,
  onDelete,
}: AudiencesTableV2RowProps) {
  const { t } = useTranslation('audiences');
  const navigate = useNavigate();
  const { downloadUrl: avatarUrl } = useGetDownloadUrl(persona.photoAssetId ?? '');

  const detailRoute = ROUTES.KNOWLEDGE_HUB_AUDIENCE_DETAIL(persona.id);
  const label =
    persona.personaTitle || persona.segmentName || t('audiences.audiencesTableV2.untitled');
  const traits = selectDistinctiveTraits(persona, gwiEnabled);
  const isReady = persona.creationStatus === 'ready';
  const isDefault = persona.type === 'Default';

  return (
    <DatatableRow
      interactive
      data-testid="audience-row"
      // `group` is what the kebab's hover reveal below keys off.
      className="group h-20"
      onClick={() => void navigate(detailRoute)}
    >
      <DatatableCell>
        <PreResolvedAvatarCell
          name={label}
          avatarUrl={avatarUrl || null}
          truncate
          // A real link as well as the row's click handler: `DatatableRow`'s
          // `interactive` is styling only and a `<tr>` has neither focus nor
          // activation semantics, so the link is the only way in from the
          // keyboard, and it buys middle-click too.
          displayNameOverride={
            <Link
              to={detailRoute}
              className="truncate text-sm text-foreground hover:underline"
              // The row navigates too; without this the same route is pushed twice.
              onClick={(event) => event.stopPropagation()}
            >
              {label}
            </Link>
          }
        />
      </DatatableCell>

      <DatatableCell emphasis="subtle">
        {traits.length > 0 ? (
          <div className="flex flex-wrap gap-xs">
            {traits.map((trait) => (
              <Badge key={trait.label} variant="secondary" roundness="full" size="sm">
                {t('audiences.audiencesGridV2.trait', {
                  label: trait.label,
                  multiplier: trait.multiplier,
                })}
              </Badge>
            ))}
          </div>
        ) : (
          EM_DASH
        )}
      </DatatableCell>

      <DatatableCell emphasis="subtle">{persona.topicOfInterest || EM_DASH}</DatatableCell>

      <DatatableCell>
        <AudienceStatusChip status={persona.creationStatus} />
      </DatatableCell>

      <DatatableCell emphasis="subtle">
        {persona.updated
          ? // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input rather than translator-facing copy
            format(new Date(persona.updated), 'MMM d, yyyy')
          : EM_DASH}
      </DatatableCell>

      {showActions && (
        <DatatableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('audiences.audiencesTableV2.actions')}
                onClick={(e) => e.stopPropagation()}
                // Revealed by the row, and held open while its own menu is: the
                // pointer leaves the row to reach the menu, which would
                // otherwise take the trigger out from under it. `focus-visible`
                // is what keeps it reachable by keyboard.
                className="opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
              >
                <MoreVerticalIcon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onEdit &&
                (isDefault ? (
                  <DisabledReasonTooltip
                    reason={t('audiences.audiencesTableV2.defaultCannotBeEdited')}
                  >
                    <DropdownMenuItem disabled>
                      {t('audiences.audiencesGridV2.edit')}
                    </DropdownMenuItem>
                  </DisabledReasonTooltip>
                ) : (
                  <DropdownMenuItem disabled={!isReady} onSelect={() => onEdit(persona)}>
                    {t('audiences.audiencesGridV2.edit')}
                  </DropdownMenuItem>
                ))}
              {onDelete &&
                (isDefault ? (
                  <DisabledReasonTooltip
                    reason={t('audiences.audiencesTableV2.defaultCannotBeDeleted')}
                  >
                    <DropdownMenuItem disabled>
                      {t('audiences.audiencesGridV2.delete')}
                    </DropdownMenuItem>
                  </DisabledReasonTooltip>
                ) : (
                  <DropdownMenuItem onSelect={() => onDelete(persona)}>
                    {t('audiences.audiencesGridV2.delete')}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </DatatableCell>
      )}
    </DatatableRow>
  );
}

AudiencesTableV2Row.displayName = 'AudiencesTableV2Row';
